<template>
    <v-container>
        <AssignComponentDialog
            :is-open="assignComponentDialog"
            :product-components="components"
            @on-save="addProductComponent"
            @on-close="closeAssignComponentDialog"
        />
        <AssignProcesesAndParametresDialog
            :is-open="assignProcesesAndParametresDialog"
            :component="currentProductionComponent"
            :properties="properties"
            :processes="processes"
            @on-close="closeAssignProcesesAndParametresDialog"
        />
        <ConfirmDialog
            :is-open="confirmRemoveComponentDialog"
            :data="currentProductionComponentIndex"
            @on-confirm="removeProductComponent(currentProductionComponentIndex)"
            @on-abort="closeConfirmRemoveComponentDialog"
        >
            <template #title>
                Usunąć komponent z produktu?
            </template>
            <template #content>
                Czy na pewno chcesz usunąć komponent?
            </template>
        </ConfirmDialog>
        <v-row>
            <v-col v-if="product.productTypeComponent.length === 0" cols="12">
                <v-alert
                    type="info"
                    text
                    border="left"
                >
                    Produkt nie posiada przypisanych komponentów.
                </v-alert>     
                <v-row>
                    <v-col 
                        lg="2"
                        md="3"
                        sm="4"
                        class="align-center xs"
                        style="min-width:170px"
                    >
                        <v-sheet
                            class="pa-3 fill-height d-flex align-center justify-center"
                            color="white"
                            elevation="1"
                            rounded
                            style="min-height:120px"
                        >
                            <v-btn
                                icon
                                outlined
                                rounded                  
                                fab
                                class="pa-3"
                                @click="showAssignComponentDialog"
                            >
                                <v-icon>
                                    mdi-plus
                                </v-icon>
                            </v-btn>
                        </v-sheet>
                    </v-col>
                </v-row>
            </v-col>
            <v-col v-else cols="12" class="pb-5">
                <v-row
                    v-if="product.productTypeComponent.length > 0"  
                    class="multiple"
                >
                    <v-col 
                        v-for="(component, componentIndex) in product.productTypeComponent"
                        :key="componentIndex"
                        lg="2"
                        md="3"
                        sm="4"
                        class="align-stretch xs"
                        style="min-width:170px"
                    >
                        <v-sheet
                            class="pa-3 pb-1 fill-height d-flex justify-space-between"
                            color="white"
                            elevation="1"
                            rounded
                            style="min-height:120px"
                        >
                            <v-row no-gutters dense class="d-flex justify-space-between fill-height">
                                <v-col cols="12">
                                    <span>
                                        <h1 class="text-subtitle-1 h-10">
                                            <template>
                                                <strong>{{ component.component.name }} ({{ component.component.code }})</strong>
                                            </template>
                                        </h1>
                                    </span>
                                </v-col>
                                <v-col cols="12 d-flex align-end">
                                    <v-container fluid class="pa-0">
                                        <v-row justify="end" dense>
                                            <v-tooltip :open-delay="tooltipDelay" top>
                                                <template #activator="{on}">
                                                    <v-btn
                                                        icon
                                                        data-sentry-label="Otwórz"
                                                        v-on="on"
                                                        @click="showAssignProductionPropertiesDialog(component, componentIndex)"
                                                    >
                                                        <v-icon color="teal">
                                                            mdi-pencil
                                                        </v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Edytuj</span>
                                            </v-tooltip>                                        
                                            <v-tooltip :open-delay="tooltipDelay" top>
                                                <template #activator="{on}">
                                                    <v-btn
                                                        icon
                                                        data-sentry-label="Usuń komponent"
                                                        v-on="on"
                                                        @click="showConfirmRemoveComponentDialog(componentIndex, $event)"
                                                    >
                                                        <v-icon color="pink">
                                                            mdi-delete
                                                        </v-icon>
                                                    </v-btn>
                                                </template>
                                                <span>Usuń komponent</span>
                                            </v-tooltip>
                                        </v-row>
                                    </v-container>
                                </v-col>
                            </v-row>
                        </v-sheet>
                    </v-col>
                    <v-col 
                        lg="2"
                        md="3"
                        sm="4"
                        class="align-center xs"
                        style="min-width:170px"
                    >
                        <v-sheet
                            class="pa-3 fill-height d-flex align-center justify-center"
                            color="white"
                            elevation="1"
                            rounded
                            style="min-height:120px"                     
                        >
                            <v-btn
                                icon
                                outlined
                                rounded                               
                                fab
                                class="pa-3"
                                @click="showAssignComponentDialog"
                            >
                                <v-icon>
                                    mdi-plus
                                </v-icon>
                            </v-btn>
                        </v-sheet>
                    </v-col>
                </v-row>
            </v-col>
        </v-row>
        <v-btn
            color="success"
            medium
            data-sentry-label="Zapisz"
            :disabled="!hasProductChanges"
            @click="saveProduct"
        >
            Zapisz
        </v-btn>
    </v-container>
</template>

<script>
import ConfirmDialog from '@/components/common/ConfirmDialog'

import AssignComponentDialog from '@/views/Configurations/Common/AssignComponentDialog'
import AssignProcesesAndParametresDialog from '@/views/Configurations/Common/AssignProcesesAndParametresDialog'

import { errorHandler } from '@/utils/errorHandler'
import tooltipDelay from '@/mixins/tooltip'
import parse from '@/mixins/parse'

import { requiredIf } from 'vuelidate/lib/validators'
import { isEqual } from 'lodash'

export default {
    name: 'ProductComponentsConfigurator',
    components: {
        ConfirmDialog,
        AssignComponentDialog,
        AssignProcesesAndParametresDialog,
    },
    mixins: [
        tooltipDelay,
        parse
    ],
    props: {
        productData: {
            type: Object,
            default: () => {}
        },
        components: {
            type: Array,
            default: () => []
        },
        properties: {
            type: Array,
            default: () => []
        },
        processes: {
            type: Array,
            default: () => []
        },
    },
    data() {
        return {
            currentProductionComponent: {
                component: {
                    name: null,
                    code: null
                },
                productTypeComponentProperties: [],
                productTypeComponentProcesses: []
            },
            currentProductionComponentIndex: null,
            product: {
                name: null,
                productTypeComponent: [],
                isDieCutterRequired: false,
                acronym: null,
                dieCuterProductPropertyId: null,
                signaturesDivision: false
            },
            assignComponentDialog: false,
            assignProcesesAndParametresDialog: false,
            confirmRemoveComponentDialog: false,
            actionLoading: {
                saveProduct: false
            }
        }
    },
    computed: {
        hasProductChanges() {
            const hasProductChanges = !isEqual(this.product, this.productData)

            this.$emit('on-product-change', hasProductChanges)

            return hasProductChanges
        },
        dieCuterProductPropertyIdErrors() {
            const errors = []

            if (!this.$v.product.dieCuterProductPropertyId.$dirty) return errors
            if (!this.$v.product.dieCuterProductPropertyId.required)
                errors.push('Cecha sklepowa jest wymagana.')

            return errors
        },
    },
    validations: {
        product: {
            dieCuterProductPropertyId: {
                required: requiredIf(function() {
                    return this.product.isDieCutterRequired
                })
            },
        }
    },
    watch: {
        productData: {
            handler: function(newValue) {
                this.product = JSON.parse(JSON.stringify(newValue))
            },
            deep: true
        }
    },
    mounted() {
        this.product = JSON.parse(JSON.stringify(this.productData))
    },
    methods: {
        showAssignComponentDialog() {
            this.assignComponentDialog = true
        },
        closeAssignComponentDialog() {
            this.assignComponentDialog = false
        },
        addProductComponent(component) {
            this.product.productTypeComponent.push({
                component: component,
                componentId: component.id,
                productTypeComponentProperties: [],
                productTypeComponentProcesses: []
            })
        },
        showConfirmRemoveComponentDialog(componentIndex, event) {
            event.cancelBubble = true
            this.confirmRemoveComponentDialog = true
            this.currentProductionComponentIndex = componentIndex
        },
        closeConfirmRemoveComponentDialog() {
            this.confirmRemoveComponentDialog = false
            this.clearCurrentProductionComponent()
        },
        removeProductComponent(componentIndex) {
            this.product.productTypeComponent.splice(componentIndex, 1)

            this.closeConfirmRemoveComponentDialog()
        },
        showAssignProductionPropertiesDialog(component) {
            this.assignProcesesAndParametresDialog = true
            this.currentProductionComponent = component
        },
        closeAssignProcesesAndParametresDialog() {
            this.assignProcesesAndParametresDialog = false
        },
        saveProduct() {
            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$store.dispatch('showNotification', {
                    type: 'error',
                    message: this.$i18n.t('validations.invalidMessage')
                })

                return
            }

            this.actionLoading.saveProduct = true
            this.$store.dispatch('configurations/saveProduct', {
                id: this.product.id,
                payload: this.product
            })
                .then(response => {
                    this.$emit('update-product-data', response.data)
                })
                .catch(error => {
                    errorHandler(error)
                })
                .finally(() => {
                    this.actionLoading.saveProduct = false
                })
        },
        clearCurrentProductionComponent() {
            this.currentProductionComponent = {
                index: null,
                component: {
                    name: null,
                    code: null
                },
                productTypeComponentProperties: [],
                productTypeComponentProcesses: []
            }
        },
    }
}
</script>